import type { HTMLAttributes, ReactNode } from 'react';
import { Check, Search, X } from 'lucide-react';
import type { UseQueryResult } from '@tanstack/react-query';
import { cn } from '@/lib/cn';
import { initials } from '@/lib/format';
import { Input } from './Field';
import { ErrorState } from './feedback';

/* -------------------------------- Avatar -------------------------------- */

const avatarTints = [
  'bg-amethyst-100 text-amethyst-800',
  'bg-sky-100 text-sky-800',
  'bg-emerald-100 text-emerald-800',
  'bg-rose-100 text-rose-800',
  'bg-indigo-100 text-indigo-800',
  'bg-teal-100 text-teal-800',
] as const;

function tintFor(name: string): string {
  let hash = 0;
  for (const char of name) {
    hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  }
  return avatarTints[hash % avatarTints.length] ?? avatarTints[0];
}

export function Avatar({ name, size = 'md', className }: { name: string; size?: 'sm' | 'md' | 'lg'; className?: string }) {
  return (
    <span
      aria-hidden
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full font-display font-bold',
        size === 'sm' && 'size-7 text-2xs',
        size === 'md' && 'size-9 text-xs',
        size === 'lg' && 'size-12 text-base',
        tintFor(name),
        className,
      )}
    >
      {initials(name)}
    </span>
  );
}

/* ------------------------------ ProgressBar ----------------------------- */

export function ProgressBar({
  value,
  max = 100,
  tone = 'brand',
  label,
  className,
}: {
  value: number;
  max?: number;
  tone?: 'brand' | 'success' | 'warning' | 'danger';
  /** Accessible description, e.g. "Occupancy". */
  label: string;
  className?: string;
}) {
  const percent = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
  const fill = { brand: 'bg-amethyst-600', success: 'bg-emerald-500', warning: 'bg-amber-500', danger: 'bg-red-500' }[tone];
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuenow={Math.round(percent)}
      aria-valuemin={0}
      aria-valuemax={100}
      className={cn('h-2 w-full overflow-hidden rounded-full bg-slate-100', className)}
    >
      <div className={cn('h-full rounded-full transition-[width]', fill)} style={{ width: `${percent}%` }} />
    </div>
  );
}

/* ----------------------------- SearchInput ------------------------------ */

export function SearchInput({
  value,
  onChange,
  placeholder = 'Search',
  label,
  className,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Accessible name (placeholder alone is not a label). */
  label?: string;
  className?: string;
}) {
  return (
    <div className={cn('relative w-full sm:max-w-xs', className)}>
      <Input
        type="search"
        aria-label={label ?? placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        leading={<Search className="size-4" aria-hidden />}
        className="pr-9 [&::-webkit-search-cancel-button]:hidden"
      />
      {value && (
        <button
          type="button"
          aria-label="Clear search"
          onClick={() => onChange('')}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
        >
          <X className="size-4" aria-hidden />
        </button>
      )}
    </div>
  );
}

/* ----------------------------- DescriptionList --------------------------- */

export function DescriptionList({ children, className }: { children: ReactNode; className?: string }) {
  return <dl className={cn('grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2', className)}>{children}</dl>;
}

export function DescriptionItem({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  return (
    <div className={className}>
      <dt className="text-xs font-semibold text-slate-500">{label}</dt>
      <dd className="mt-0.5 text-sm text-slate-900">{children}</dd>
    </div>
  );
}

/* -------------------------------- Stepper -------------------------------- */

export function Stepper({ steps, current, className }: { steps: readonly string[]; current: number; className?: string }) {
  return (
    <ol className={cn('flex items-center gap-2', className)} aria-label="Progress">
      {steps.map((step, index) => {
        const done = index < current;
        const active = index === current;
        return (
          <li key={step} aria-current={active ? 'step' : undefined} className="flex flex-1 items-center gap-2 last:flex-none">
            <span
              className={cn(
                'flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-bold',
                done && 'bg-amethyst-700 text-white',
                active && 'bg-amethyst-100 text-amethyst-800 ring-2 ring-amethyst-600',
                !done && !active && 'bg-slate-100 text-slate-500',
              )}
            >
              {done ? <Check className="size-4" aria-hidden /> : index + 1}
            </span>
            <span className={cn('hidden text-xs font-semibold sm:inline', active ? 'text-slate-900' : 'text-slate-500')}>{step}</span>
            {index < steps.length - 1 && <span className={cn('h-px flex-1', done ? 'bg-amethyst-600' : 'bg-slate-200')} aria-hidden />}
          </li>
        );
      })}
    </ol>
  );
}

/* --------------------------------- Kbd ----------------------------------- */

export function Kbd({ children, className, ...rest }: HTMLAttributes<HTMLElement>) {
  return (
    <kbd
      className={cn('rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-sans text-2xs font-semibold text-slate-500', className)}
      {...rest}
    >
      {children}
    </kbd>
  );
}

/* ----------------------------- QueryBoundary ----------------------------- */

export interface QueryBoundaryProps<T> {
  query: UseQueryResult<T>;
  /** Placeholder while the first load is in flight. Match the final layout. */
  loading: ReactNode;
  /** Returns true when the loaded data counts as "nothing to show". */
  isEmpty?: (data: T) => boolean;
  empty?: ReactNode;
  children: (data: T) => ReactNode;
}

/**
 * Gives a page all four states from one query: skeleton, error with retry, empty with a
 * next step, and the populated view. Use it for any section backed by a single query.
 */
export function QueryBoundary<T>({ query, loading, isEmpty, empty, children }: QueryBoundaryProps<T>) {
  if (query.isPending) {
    return <>{loading}</>;
  }
  if (query.isError) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} compact />;
  }
  if (isEmpty?.(query.data)) {
    return <>{empty}</>;
  }
  return <>{children(query.data)}</>;
}
